import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Stack,
  Grid,
  CircularProgress,
  Button,
  InputAdornment,
  TextField,
  Select,
  MenuItem,
  Snackbar,
  Alert,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import SearchIcon from "@mui/icons-material/Search";
import InvitedCandidate from "./invite-candidates";

const ClosePosition = ({ candidates }) => {
  const [positions, setPositions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [selectedPositionForCandidates, setSelectedPositionForCandidates] =
    useState(null);
  const [openCandidateHistoryDialog, setOpenCandidateHistoryDialog] =
    useState(false);
  const [candidateHistoryStatus, setCandidateHistoryStatus] = useState("");
  const [message, setMessage] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const [severity, setSeverity] = useState("success");
  const showSnackbar = (msg, type) => {
    setMessage(msg);
    setSeverity(type);
    setOpen(true);
  };

  useEffect(() => {
    fetchPositions();
  }, [searchTerm, sortBy]);

  const fetchPositions = async () => {
    setLoading(true);

    try {
      const response = await axios.get(
        `https://api.a2ajobs.com/api/position-close-list?search=${searchTerm}&sortby=${sortBy}`
      );
      setPositions(response.data);
    } catch (error) {
      console.error("Error fetching positions:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (positionId) => {
    if (!window.confirm("Are you sure you want to delete this position?"))
      return;

    try {
      await axios.post(
        "https://api.a2ajobs.com/api/position-delete",
        { position_id: positionId },
        { headers: { "Content-Type": "application/json" } }
      );
      setPositions((prevPositions) => ({
        ...prevPositions,
        data: prevPositions.data.filter((pos) => pos.id !== positionId),
      }));

      showSnackbar("Position deleted successfully!", "success");
    } catch (error) {
      console.error("Error deleting position:", error);
      alert("Failed to delete position!");
    }
  };
  const handleOpenPosition = async (positionId) => {
    if (!window.confirm("Are you sure you want to open this position?")) return;

    try {
      const response = await axios.post(
        "https://api.a2ajobs.com/api/position-open",
        { position_id: positionId },
        { headers: { "Content-Type": "application/json" } }
      );

      setPositions((prevPositions) => ({
        ...prevPositions,
        data: prevPositions.data.filter((pos) => pos.id !== positionId),
      }));

      showSnackbar(
        response.data.message || "Position opened successfully!!",
        "success"
      );
    } catch (error) {
      console.error(
        "Error opening position:",
        error.response?.data || error.message
      );
      alert("Failed to open position!");
    }
  };
  const handleOpenCandidateHistory = (position, status) => {
    setSelectedPositionForCandidates(position);
    setCandidateHistoryStatus(status);
    setOpenCandidateHistoryDialog(true);
  };
  const filteredPositions = positions?.data?.filter((pos) =>
    pos.title?.toLowerCase().includes(searchTerm)
  );
  return (
    <Box
      sx={{
        maxWidth: "100%",
        margin: "auto",
        p: { md: 2, xs: 0 },
        width: "97%",
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        mb={2}
        alignItems="center"
        justifyContent="flex-end"
      >
        <TextField
          variant="outlined"
          size="small"
          placeholder="Search positions here"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            height: "100%",
            ".MuiOutlinedInput-root": {
              height: "100%",
              borderRadius: "10px",
              backgroundColor: "common.grey",
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="disabled" />
              </InputAdornment>
            ),
          }}
        />
        <Select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          displayEmpty
          size="small"
          sx={{ bgcolor: "white", borderRadius: 2, minWidth: "120px" }}
        >
          <MenuItem value="">Sort by </MenuItem>
          <MenuItem value="a-z">A-Z</MenuItem>
          <MenuItem value="z-a">Z-A</MenuItem>
        </Select>
      </Stack>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress size={50} />
        </Box>
      ) : (
        <Box>
          {filteredPositions?.length > 0 ? (
            filteredPositions.map((pos, index) => {
              const positionCandidates = candidates.filter(
                (candidate) => candidate.position_id === pos.id
              );
              const invitedCount = positionCandidates.filter(
                (c) => c.status === "invited"
              ).length;
              const toEvaluateCount = positionCandidates.filter(
                (c) => c.status === "evaluate"
              ).length;
              const shortlistedCount = positionCandidates.filter(
                (c) => c.status === "shortlisted"
              ).length;
              const discardedCount = positionCandidates.filter(
                (c) => c.status === "discarded"
              ).length;

              return (
                <Card key={index} sx={{ mb: 2 }}>
                  <CardContent>
                    <Stack p={{ xs: 1, md: 0 }} borderRadius={2}>
                      <Typography
                        variant="h6"
                        sx={{ fontSize: { xs: "18px", md: "18px" } }}
                      >
                        {pos.title}
                      </Typography>
                      <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
                        <Chip
                          label={`Invited (${invitedCount})`}
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenCandidateHistory(pos, "invited");
                          }}
                        />
                        <Chip
                          label={`To Evaluate (${toEvaluateCount})`}
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenCandidateHistory(pos, "evaluate");
                          }}
                        />
                        <Chip
                          label={`Shortlisted (${shortlistedCount})`}
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenCandidateHistory(pos, "shortlisted");
                          }}
                        />
                        <Chip
                          label={`Discarded (${discardedCount})`}
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenCandidateHistory(pos, "discarded");
                          }}
                        />
                      </Stack>
                      <Stack
                        direction="row"
                        spacing={1}
                        mt={{ xs: 1, md: -5 }}
                        justifyContent="end"
                      >
                        <Button
                          variant="outlined"
                          sx={{
                            borderRadius: "50px",
                            padding: { xs: "6px 15px", md: "8px 20px" },
                            fontWeight: 500,
                            borderColor: "#1976d2",
                            color: "#1976d2",
                            textTransform: "none",
                            fontSize: { xs: "14px", md: "16px" },
                            minWidth: { xs: "120px", md: "150px" },
                          }}
                          onClick={() => handleOpenPosition(pos.id)}
                        >
                          Open Position
                        </Button>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(pos.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })
          ) : (
            <Grid item xs={12}>
              <Typography variant="h5" align="center">
                No Data Found
              </Typography>
            </Grid>
          )}{" "}
        </Box>
      )}

      {openCandidateHistoryDialog && selectedPositionForCandidates && (
        <InvitedCandidate
          open={openCandidateHistoryDialog}
          onClose={() => setOpenCandidateHistoryDialog(false)}
          position={selectedPositionForCandidates}
          questions={[]}
          initialTab={candidateHistoryStatus}
        />
      )}
      <Snackbar
        open={open}
        autoHideDuration={3000}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setOpen(false)}
          severity={severity}
          variant="filled"
        >
          {message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
export default ClosePosition;
